import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import es from './es';
import en from './en';

const dict = { es, en };
type Lang = 'es' | 'en';

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (path: string, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = 'edd_lang';

/**
 * Traduce una clave usando dot notation
 */
function translate(lang: Lang, path: string, params?: Record<string, string | number>): string {
  const keys = path.split('.');
  let value: any = dict[lang];
  
  for (const k of keys) {
    value = value?.[k];
  }
  
  if (typeof value !== 'string') {
    return path;
  }
  
  if (params) {
    return value.replace(/\{(\w+)\}/g, (match, key) => {
      return params[key]?.toString() ?? match;
    });
  }
  
  return value;
}

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps) {
  // Inicializar desde localStorage o default 'es'
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY) as Lang | null;
      if (saved === 'es' || saved === 'en') {
        return saved;
      }
    }
    return 'es';
  });

  // Función para cambiar idioma con persistencia
  const setLang = (newLang: Lang) => {
    setLangState(newLang);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, newLang);
    }
  };

  // Función t que usa el idioma actual
  const t = (path: string, params?: Record<string, string | number>) => {
    return translate(lang, path, params);
  };

  const value: I18nContextValue = {
    lang,
    setLang,
    t,
  };

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

/**
 * Hook para usar i18n con reactividad
 */
export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n debe usarse dentro de I18nProvider');
  }
  return context;
}
